import { useEffect, useMemo } from "react";
import { useMatch } from "react-router-dom";

import { usePublishedLocationsQuery } from "@/features/dashboard/features/dashboard";
import {
  useDashboardStoreActions,
  useSelectedLocationId,
} from "@/features/dashboard/store";

import LocationSelector from "./location-selector/location-selector";
import TimeRangeSelector from "./timerange-selector/timerange-selector";
import styles from "./time-location-selector.module.css";

interface TimeLocationSelectorProps {
  dashboardId?: string;
  showLocationSelector: boolean;
  showTimeRangeSelector: boolean;
}

export default function TimeLocationSelector({
  dashboardId,
  showLocationSelector,
  showTimeRangeSelector,
}: TimeLocationSelectorProps) {
  const locationsQuery = usePublishedLocationsQuery(dashboardId);
  const { setSelectedLocationId } = useDashboardStoreActions();
  const selectedLocationId = useSelectedLocationId();
  const { params } = useMatch("/:dashboard/*") ?? {};

  const isSelectedLocationInLocations = useMemo(
    () => locationsQuery.data?.some((location) => location.id === selectedLocationId),
    [locationsQuery.data, selectedLocationId],
  );

  useEffect(() => {
    if (!isSelectedLocationInLocations && locationsQuery.data?.length) {
      setSelectedLocationId(locationsQuery.data?.[0]?.id);
    }
  }, [
    params?.dashboard,
    locationsQuery.data,
    isSelectedLocationInLocations,
    setSelectedLocationId,
  ]);

  if (!dashboardId) return null;

  return (
    <div className={styles.timeLocationBox}>
      <div className={styles.timeLocationStack}>
        {showTimeRangeSelector && <TimeRangeSelector />}
        {showLocationSelector && <LocationSelector dashboardId={dashboardId} />}
      </div>
    </div>
  );
}
